//#region src/utils/draw-relationship/incremental.ts
var ROUTE_TOUCH = 40;
var LANE_INFLUENCE = 64;
var EXTENT_SLACK = 16;
var BUCKET = 64;
var MAX_MOVED_TABLES = 64;
var caches = /* @__PURE__ */ new WeakMap();
var createSortCache = () => ({
	boxes: /* @__PURE__ */ new Map(),
	entries: /* @__PURE__ */ new Map()
});
function getSortCache(state, source = "document") {
	let record = caches.get(state);
	if (!record) {
		record = {
			document: createSortCache(),
			flow: createSortCache()
		};
		caches.set(state, record);
	}
	return record[source];
}
function boxOf(obstacles, index) {
	return {
		left: obstacles.left[index],
		top: obstacles.top[index],
		right: obstacles.right[index],
		bottom: obstacles.bottom[index]
	};
}
function union(a, b) {
	return {
		left: Math.min(a.left, b.left),
		top: Math.min(a.top, b.top),
		right: Math.max(a.right, b.right),
		bottom: Math.max(a.bottom, b.bottom)
	};
}
function same(a, b) {
	return a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom;
}
function overlaps(a, b) {
	return a.left <= b.right && a.right >= b.left && a.top <= b.bottom && a.bottom >= b.top;
}
function routeTouches(bounds, boxes) {
	const reach = {
		left: bounds.left - ROUTE_TOUCH,
		top: bounds.top - ROUTE_TOUCH,
		right: bounds.right + ROUTE_TOUCH,
		bottom: bounds.bottom + ROUTE_TOUCH
	};
	for (const box of boxes) if (overlaps(reach, box)) return true;
	return false;
}
function createDirtyLanes() {
	const verticalLanes = /* @__PURE__ */ new Map();
	const horizontalLanes = /* @__PURE__ */ new Map();
	const markSpan = (vertical, from, to, low, high) => {
		const bucket = vertical ? verticalLanes : horizontalLanes;
		const first = Math.floor(Math.min(from, to) / BUCKET);
		const last = Math.floor(Math.max(from, to) / BUCKET);
		for (let key = first; key <= last; key++) {
			const list = bucket.get(key);
			if (list) list.push(from, to, low, high);
			else bucket.set(key, [
				from,
				to,
				low,
				high
			]);
		}
	};
	return {
		markSpan,
		markBox(box) {
			markSpan(true, box.left, box.right, box.top, box.bottom);
			markSpan(false, box.top, box.bottom, box.left, box.right);
		},
		hits(vertical, from, to, low, high) {
			const bucket = vertical ? verticalLanes : horizontalLanes;
			if (!bucket.size) return false;
			const near = Math.min(from, to) - LANE_INFLUENCE;
			const far = Math.max(from, to) + LANE_INFLUENCE;
			const first = Math.floor(near / BUCKET);
			const last = Math.floor(far / BUCKET);
			for (let key = first; key <= last; key++) {
				const list = bucket.get(key);
				if (!list) continue;
				for (let index = 0; index < list.length; index += 4) {
					if (list[index + 1] < near || list[index] > far) continue;
					if (list[index + 3] < low - EXTENT_SLACK) continue;
					if (list[index + 2] > high + EXTENT_SLACK) continue;
					return true;
				}
			}
			return false;
		}
	};
}
function markRoute(lanes, points) {
	for (let index = 1; index < points.length; index++) {
		const a = points[index - 1];
		const b = points[index];
		if (Math.abs(a.x - b.x) < .5) lanes.markSpan(true, a.x, b.x, Math.min(a.y, b.y), Math.max(a.y, b.y));
		else lanes.markSpan(false, a.y, b.y, Math.min(a.x, b.x), Math.max(a.x, b.x));
	}
}
function diffTableBoxes(cache, obstacles) {
	const previous = cache.boxes;
	const next = /* @__PURE__ */ new Map();
	const dirty = [];
	let full = false;
	for (let index = 0; index < obstacles.ids.length; index++) {
		const id = obstacles.ids[index];
		const box = boxOf(obstacles, index);
		next.set(id, box);
		if (full) continue;
		const was = previous.get(id);
		if (!was) dirty.push(box);
		else if (!same(was, box)) dirty.push(union(was, box));
		if (dirty.length > MAX_MOVED_TABLES) full = true;
	}
	if (!full) for (const [id, box] of previous) {
		if (next.has(id)) continue;
		dirty.push(box);
		if (dirty.length > MAX_MOVED_TABLES) {
			full = true;
			break;
		}
	}
	cache.boxes = next;
	return full || previous.size === 0 ? null : dirty;
}
//#endregion
export { createDirtyLanes, diffTableBoxes, getSortCache, markRoute, routeTouches, union };
